'use client';

// ═══════════════════════════════════════════════════════════════════════════
// BANDEAU COOKIES — site entier (racine app/layout.js), un seul exemplaire.
// ═══════════════════════════════════════════════════════════════════════════
//
// Remplace l'ancien bandeau local à app/vitrine/page.js (n'apparaissait que
// sur « / », donc pas sur le blog, les pages secteurs, l'app connectée — tout
// ce que app/layout.js sert aussi). Un seul non-essentiel existe aujourd'hui
// (l'analytics PostHog) : deux boutons Accepter/Refuser suffisent, pas de
// panneau de préférences par catégorie (RGPD n'exige la granularité que s'il
// y a plusieurs finalités distinctes à choisir séparément).
//
// FR/NL uniquement (R8 : EN/DE abandonnés le 11/07/2026). Langue lue via
// getCurrentLang() — ce bandeau vit AU-DESSUS de tous les LangProvider
// (dashboard, vitrine), donc il ne peut pas consommer leur contexte React ;
// getCurrentLang() est le mécanisme server-safe déjà prévu pour ce cas.

import { useEffect, useRef, useState } from 'react';
import { getConsent, setConsent, CONSENT_EVENT } from '@/app/lib/cookie-consent';
import { getCurrentLang } from '@/app/lib/lang-context';

const TXT = {
  fr: {
    text: "Nous utilisons un cookie de mesure d'audience (PostHog, hébergé en UE) uniquement si vous l'acceptez. Aucun cookie non essentiel n'est déposé sans votre accord.",
    more: 'En savoir plus',
    accept: 'Accepter',
    refuse: 'Refuser',
  },
  nl: {
    text: 'We gebruiken een bezoekersanalyse-cookie (PostHog, gehost in de EU) enkel als u dat aanvaardt. Geen enkel niet-essentieel cookie wordt geplaatst zonder uw toestemming.',
    more: 'Meer weten',
    accept: 'Aanvaarden',
    refuse: 'Weigeren',
  },
};

export default function CookieBanner() {
  // `undefined` = pas encore lu. On ne lit le localStorage qu'APRÈS le montage :
  // le serveur ne le connaît pas, donc lire dès l'initialisation faisait rendre
  // au serveur un bandeau que le client (consentement déjà donné) ne rendait
  // pas — erreur d'hydratation à chaque chargement, et un bandeau mort tant que
  // la page n'est pas hydratée. Aucun bandeau n'est donc émis côté serveur.
  const [consent, setConsentState] = useState(undefined);
  const [lang, setLangState] = useState('fr');
  const barRef = useRef(null);

  useEffect(() => {
    setLangState(getCurrentLang());
    setConsentState(getConsent());
    const onChange = (e) => setConsentState(e?.detail?.consent ?? getConsent());
    window.addEventListener(CONSENT_EVENT, onChange);
    return () => window.removeEventListener(CONSENT_EVENT, onChange);
  }, []);

  const visible = consent === null;

  // Le bandeau est collé en bas de l'écran : tout ce qui se trouve en dessous
  // (bouton « Connexion » du menu mobile, pied de page, formulaires) devenait
  // inatteignable. Il publie donc sa hauteur : la page se réserve cette place
  // (padding du body) et les panneaux plein écran la lisent via la variable
  // CSS --cookie-banner-h. Tout est retiré dès que le bandeau disparaît.
  useEffect(() => {
    if (!visible) return undefined;
    const el = barRef.current;
    if (!el) return undefined;
    const root = document.documentElement;
    const body = document.body;
    const previousPadding = body.style.paddingBottom;
    const publish = () => {
      const h = el.offsetHeight;
      root.style.setProperty('--cookie-banner-h', `${h}px`);
      body.style.paddingBottom = `${h}px`;
    };
    publish();
    let observer = null;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(publish);
      observer.observe(el);
    }
    window.addEventListener('resize', publish);
    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener('resize', publish);
      root.style.removeProperty('--cookie-banner-h');
      body.style.paddingBottom = previousPadding;
    };
  }, [visible]);

  if (!visible) return null;

  const t = TXT[lang] || TXT.fr;

  return (
    <div
      ref={barRef}
      className="au-cookie-bar"
      role="dialog"
      aria-label={lang === 'nl' ? 'Cookievoorkeuren' : 'Préférences cookies'}
      style={{
        position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 9999,
        background: '#0a0908', borderTop: '1px solid rgba(255,255,255,.1)',
        padding: '18px 28px', display: 'flex', alignItems: 'center',
        justifyContent: 'space-between', gap: 20, flexWrap: 'wrap',
        boxShadow: '0 -8px 40px rgba(0,0,0,.35)',
        fontFamily: "'Cabinet Grotesk', Inter, system-ui, sans-serif",
      }}
    >
      {/* Téléphone : barre compacte (texte plus petit, boutons sur une ligne) —
          sinon elle mange le quart de l'écran. */}
      <style>{`
        @media (max-width: 640px) {
          .au-cookie-bar { padding: 12px 16px calc(12px + env(safe-area-inset-bottom, 0px)) !important; gap: 10px !important; }
          .au-cookie-bar p { font-size: 12px !important; line-height: 1.4 !important; min-width: 0 !important; flex-basis: 100% !important; }
          .au-cookie-bar .au-cookie-actions { width: 100%; }
          .au-cookie-bar .au-cookie-actions button { flex: 1; padding-top: 10px !important; padding-bottom: 10px !important; }
        }
      `}</style>
      <p style={{ fontSize: 13.5, color: 'rgba(255,255,255,.75)', margin: 0, flex: 1, minWidth: 240, lineHeight: 1.5 }}>
        {t.text}{' '}
        <a
          href={`/legal/privacy${lang === 'nl' ? '?lang=nl' : ''}`}
          target="_blank" rel="noopener noreferrer"
          style={{ color: '#c6a34e', textDecoration: 'none' }}
        >
          {t.more} ↗
        </a>
      </p>
      <div className="au-cookie-actions" style={{ display: 'flex', gap: 10, flexShrink: 0 }}>
        <button
          onClick={() => setConsent(false)}
          style={{
            padding: '9px 18px', borderRadius: 6, background: 'transparent',
            color: 'rgba(255,255,255,.55)', border: '1px solid rgba(255,255,255,.2)',
            cursor: 'pointer', fontSize: 13, fontFamily: 'inherit',
          }}
        >
          {t.refuse}
        </button>
        <button
          onClick={() => setConsent(true)}
          style={{
            padding: '9px 20px', borderRadius: 6, border: 'none',
            background: 'linear-gradient(135deg,#d9b45a,#c6a34e)', color: '#0a0908',
            cursor: 'pointer', fontSize: 13, fontWeight: 700, fontFamily: 'inherit',
          }}
        >
          {t.accept}
        </button>
      </div>
    </div>
  );
}
